import { useEffect, useState } from "react";
import { toast } from "sonner";

import { ProfessionalFields } from "@/components/profile/ProfessionalFields";
import { SignatureUploader } from "@/components/profile/SignatureUploader";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const EMPTY = { titulo: "Dr.", especialidad: "", cedula: "", universidad: "", telefono: "", direccion: "", payment_link: "" };

function useProfileForm(open, user) {
  const [form, setForm] = useState(EMPTY);
  const [name, setName] = useState("");
  const [whatsapp, setWhatsapp] = useState("");

  useEffect(() => {
    if (open && user) {
      setForm({ ...EMPTY, ...(user.professional_profile || {}) });
      setName(user.name || "");
      setWhatsapp(user.whatsapp || "");
    }
  }, [open, user]);

  return { form, setForm, name, setName, whatsapp, setWhatsapp };
}

export function ProfileDialog({ open, onOpenChange }) {
  const { user, setUser } = useAuth();
  const { form, setForm, name, setName, whatsapp, setWhatsapp } = useProfileForm(open, user);
  const [saving, setSaving] = useState(false);

  const isPro = user?.role === "professional" || user?.role === "admin";

  const save = async () => {
    setSaving(true);
    try {
      const payload = { name, whatsapp };
      if (isPro) payload.professional_profile = form;
      const { data } = await api.put("/auth/profile", payload);
      setUser(data);
      toast.success("Perfil actualizado");
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">Mi perfil</DialogTitle>
          <DialogDescription>
            {isPro
              ? "Estos datos aparecerán en el encabezado de tus recetas. Tu WhatsApp será visible para tus pacientes."
              : "Tu WhatsApp será visible para tu especialista, para que pueda contactarte."}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5 col-span-2">
            <Label>Nombre completo</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name-input" />
          </div>
          <div className="space-y-1.5 col-span-2">
            <Label>WhatsApp</Label>
            <Input
              value={whatsapp}
              onChange={(e) => setWhatsapp(e.target.value)}
              placeholder="+52 55 1234 5678"
              data-testid="profile-whatsapp-input"
            />
          </div>
          {isPro && (
            <ProfessionalFields form={form} onChange={setForm} showPaymentLink={isPro} />
          )}
        </div>
        {isPro && <SignatureUploader user={user} />}
        <DialogFooter>
          <Button onClick={save} disabled={saving} className="rounded-full" data-testid="profile-save-btn">
            {saving ? "Guardando..." : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
